import { Lunar, LunarYear } from 'lunar-javascript'

// 节日提醒工具：支持按公历每年重复的节日，和指定年份的节日（便于添加农历节日的当年日期）
export type Holiday = {
  name: string
  // 日期格式：
  // - "MM-DD" 表示每年重复（公历）
  // - "YYYY-MM-DD" 表示仅该年的具体日期（例如农历节日在当年的公历日期）
  date: string
}

// 每年固定日期（公历）
export const HOLIDAYS: Holiday[] = [
  { name: '元旦', date: '01-01' },
  { name: '劳动节', date: '05-01' },
  { name: '国庆节', date: '10-01' },
  { name: '儿童节', date: '06-01' },
  { name: '教师节', date: '09-10' },
]

// 将农历转为当年的公历 YYYY-MM-DD 字符串
function toSolarYmd(year: number, lunarMonth: number, lunarDay: number): string | null {
  try {
    const lunar = Lunar.fromYmd(year, lunarMonth, lunarDay)
    const solar = lunar.getSolar()
    return solar.toYmd() // YYYY-MM-DD
  } catch {
    // 容错：若年份超界或库报错，忽略该条
    return null
  }
}

// 帮助函数：尽量使用 unknown 并做类型收缩，避免 any
type FromYearFn = (y: number) => unknown

function tryGetLunarYear(year: number): unknown {
  const LY = LunarYear as unknown as { fromYear?: FromYearFn }
  if (typeof LY?.fromYear === 'function') return LY.fromYear(year)
  const L = Lunar as unknown as { Year?: { fromYear?: FromYearFn } }
  if (typeof L.Year?.fromYear === 'function') return L.Year.fromYear(year)
  return undefined
}

function tryGetJieQiTable(ly: unknown): unknown {
  const obj = ly as { getJieQiTable?: () => unknown } | undefined
  if (obj && typeof obj.getJieQiTable === 'function') return obj.getJieQiTable()
  return undefined
}

function tryGetFromTable(table: unknown, key: string): unknown {
  if (!table) return undefined
  const maybeMap = table as { get?: (k: string) => unknown }
  if (maybeMap && typeof maybeMap.get === 'function') {
    const v = maybeMap.get(key)
    if (v) return v
  }
  if (typeof table === 'object' && table !== null && key in (table as Record<string, unknown>)) {
    return (table as Record<string, unknown>)[key]
  }
  return undefined
}

function tryGetQingmingFromLy(ly: unknown): string | null {
  const withGetJieQi = ly as { getJieQi?: (name: string) => unknown } | undefined
  if (withGetJieQi && typeof withGetJieQi.getJieQi === 'function') {
    const q = withGetJieQi.getJieQi('清明')
    if (q) {
      const withToYmd = q as { toYmd?: () => string }
      if (typeof withToYmd.toYmd === 'function') return withToYmd.toYmd()
      const withGetSolar = q as { getSolar?: () => { toYmd: () => string } }
      if (typeof withGetSolar.getSolar === 'function') return withGetSolar.getSolar().toYmd()
    }
  }
  return null
}

// 新增：获取清明节的公历日期（节气）。优先使用库，失败回退为 04-05。
function getQingMingYmd(year: number): string {
  try {
    const ly = tryGetLunarYear(year)
    const table = tryGetJieQiTable(ly)
    const qingming = tryGetFromTable(table, '清明')
    if (qingming) {
      const withToYmd = qingming as { toYmd?: () => string }
      if (typeof withToYmd.toYmd === 'function') return withToYmd.toYmd()
      const withGetSolar = qingming as { getSolar?: () => { toYmd: () => string } }
      if (typeof withGetSolar.getSolar === 'function') return withGetSolar.getSolar().toYmd()
    }
    const alt = tryGetQingmingFromLy(ly)
    if (alt) return alt
  } catch {}
  return `${year}-04-05`
}

// 生成指定年份的农历节日（春节1-1、端午5-5、中秋8-15、七夕7-7）+ 清明节（节气）对应公历日期
function generateLunarHolidays(year: number): Holiday[] {
  const items: Holiday[] = []
  const map: Array<{ name: string; m: number; d: number }> = [
    { name: '春节', m: 1, d: 1 },
    { name: '元宵节', m: 1, d: 15 },
    { name: '端午节', m: 5, d: 5 },
    { name: '七夕节', m: 7, d: 7 },
    { name: '中元节', m: 7, d: 15 },
    { name: '中秋节', m: 8, d: 15 },  
    { name: '重阳节', m: 9, d: 9 },
    { name: '小年', m: 12, d: 23 },
    { name: '除夕', m: 12, d: 30 }
  ]
  for (const it of map) {
    const ymd = toSolarYmd(year, it.m, it.d)
    if (ymd) items.push({ name: it.name, date: ymd })
  }
  const qingming = getQingMingYmd(year)
  if (qingming) items.push({ name: '清明节', date: qingming })
  return items
}

function parseDateWithYear(year: number, mmdd: string): Date {
  const [mm, dd] = mmdd.split('-').map((s) => parseInt(s, 10))
  return new Date(year, mm - 1, dd)
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

function diffDays(a: Date, b: Date): number {
  const ms = startOfDay(a).getTime() - startOfDay(b).getTime()
  return Math.round(ms / 86400000)
}

function nextOccurrence(today: Date, holiday: Holiday): Date | null {
  // YYYY-MM-DD 固定日期
  if (/^\d{4}-\d{2}-\d{2}$/.test(holiday.date)) {
    const [y, m, d] = holiday.date.split('-').map((v) => parseInt(v, 10))
    return new Date(y, m - 1, d)
  }
  // MM-DD 每年重复
  if (/^\d{2}-\d{2}$/.test(holiday.date)) {
    const thisYear = parseDateWithYear(today.getFullYear(), holiday.date)
    if (thisYear >= startOfDay(today)) return thisYear
    return parseDateWithYear(today.getFullYear() + 1, holiday.date)
  }
  return null
}

export type UpcomingHoliday = {
  name: string
  date: Date
  daysLeft: number // 0 表示今天
}

export function getUpcomingHoliday(today: Date = new Date(), withinDays = 5): UpcomingHoliday | null {
  // 动态生成：当前年与次年的农历节日（避免跨年时漏提醒）
  const year = today.getFullYear()
  const dynamicLunar = [...generateLunarHolidays(year), ...generateLunarHolidays(year + 1)]

  // 合并固定公历节日 + 动态农历节日
  const all: Holiday[] = [...HOLIDAYS, ...dynamicLunar]

  let best: UpcomingHoliday | null = null
  for (const h of all) {
    const occ = nextOccurrence(today, h)
    if (!occ) continue
    const left = diffDays(occ, today)
    if (left >= 0 && left <= withinDays) {
      if (!best || left < best.daysLeft || (left === best.daysLeft && occ < best.date)) {
        best = { name: h.name, date: occ, daysLeft: left }
      }
    }
  }
  return best
}

// 新增：返回 withinDays 天内的全部节日，按剩余天数升序排列
export function getUpcomingHolidays(
  today: Date = new Date(),
  withinDays = 5
): UpcomingHoliday[] {
  const year = today.getFullYear()
  const dynamicLunar = [...generateLunarHolidays(year), ...generateLunarHolidays(year + 1)]
  const all: Holiday[] = [...HOLIDAYS, ...dynamicLunar]

  const list: UpcomingHoliday[] = []
  for (const h of all) {
    const occ = nextOccurrence(today, h)
    if (!occ) continue
    const left = diffDays(occ, today)
    if (left >= 0 && left <= withinDays) {
      list.push({ name: h.name, date: occ, daysLeft: left })
    }
  }

  // 排序：先按天数，再按日期
  list.sort((a, b) => a.daysLeft - b.daysLeft || a.date.getTime() - b.date.getTime())

  // 去重（同名同日的潜在重复）
  const seen = new Set<string>()
  const dedup: UpcomingHoliday[] = []
  for (const item of list) {
    const key = `${item.name}-${item.date.toDateString()}`
    if (!seen.has(key)) {
      seen.add(key)
      dedup.push(item)
    }
  }
  return dedup
}
